import { PageTopBar, ThreeSlotLayout } from "../../../../../common/components/page_top_bar";
import { useFornecedoresCadastroViewModel } from "../../viewModels/fornecedores_cadastro_view_model";
import { Breadcrumbs, Typography, Link, Stack, Tooltip } from "@mui/material";
import { Link as LinkTo } from 'react-router';
import { FormSwitchInput } from "../../../../../common/components/masked_inputs/react_forms/form_switch_input";
import { BackButton } from "../../../../../common/components/back_button";
import { PopperButton } from "../../../../../common/components/popper_button";
import { FormCNPJMaskedInput } from "../../../../../common/components/masked_inputs/react_forms/form_cnpj_masked_input";
import { DefaultFormAutocomplete } from "../../../../../common/components/default_form_autocomplete";
import { FormCPFMaskedInput } from "../../../../../common/components/masked_inputs/react_forms/form_cpf_masked_input";
import { FormPhoneMaskedInput } from "../../../../../common/components/masked_inputs/react_forms/form_phone_masked_input";
import { FormDatePickerInput } from "../../../../../common/components/masked_inputs/react_forms/form_date_picker_input";
import { FormMaskedInput } from "../../../../../common/components/masked_inputs/react_forms/form_masked_input";
import { StyledDataGrid } from "../../../../../common/components/styled_data_grid";
import { useMemo } from "react";
import { FornecedorBanco } from "../../../domain/models/fornecedor_banco";
import { FornecedorAvaliacao } from "../../../domain/models/fornecedor_avaliacao";
import { FornecedorDocumento } from "../../../domain/models/fornecedor_documento";
import { FornecedorBancoModal } from "./components/fornecedor_banco_modal";
import { FornecedorAvaliacaoModal } from "./components/fornecedor_avaliacao_modal";
import { FornecedorDocumentoModal } from "./components/fornecedor_documento_modal";
import { CustomStyledDataGrid } from "./components/custom_styled_datagrid";
import { FornecedorQualificacao } from "../../../domain/models/fornecedor_qualificacao";
import { EMPTY_PINNED_COLUMN_FIELDS, GridPinnedColumnFields } from "@mui/x-data-grid-pro";
import { ChangeHistoryModal } from "../../../../../common/modals/change_history_modal";
import { isValidId } from "../../../../../common/utils/utils";

import {
  GenerateFornecedorBancoColumnDefinition,
  GenerateFornecedorAvaliacaoColumnDefinition,
  GenerateFornecedorDocumentoColumnDefinition,
  GenerateFornecedorQualificacaoColumnDefinition,
} from "./columns";

import PrimaryButton from "../../../../../common/components/primary_button";
import DynamicIcon from "../../../../../common/components/dynamic_icon";
import AlertError from "../../../../../common/components/alert_error";
import RefreshButton from "../../../../../common/components/refresh_button";
import BottomBar from "../../../../../common/components/bottom_bar";
import DefaultFormInput from "../../../../../common/components/default_form_input";
import DeleteDialog from "../../../../../common/components/delete_dialog";
import CascateList from "../../../../../common/components/cascate_list";

/**
 * FornecedoresCadastro
 */

export const FornecedoresCadastro = () => {
  const viewModel = useFornecedoresCadastroViewModel();

  const bancoColumns = useMemo(() => {
    return GenerateFornecedorBancoColumnDefinition({
      onActionEditar: viewModel.handleBancoModalOpenState,
      onActionDelete: viewModel.handleBancoDeleteModalOpenState,
    });
  }, [
    viewModel.handleBancoModalOpenState,
    viewModel.handleBancoDeleteModalOpenState,
  ]);

  const avaliacaoColumns = useMemo(() => {
    return GenerateFornecedorAvaliacaoColumnDefinition({
      onActionEditar: viewModel.handleAvaliacaoModalOpenState,
      onActionDelete: viewModel.handleAvaliacaoDeleteModalOpenState,
    });
  }, [
    viewModel.handleAvaliacaoModalOpenState,
    viewModel.handleAvaliacaoDeleteModalOpenState,
  ]);

  const documentoColumns = useMemo(() => {
    return GenerateFornecedorDocumentoColumnDefinition({
      onActionEditar: viewModel.handleDocumentoModalOpenState,
      onActionDownload: viewModel.handleDocumentoDownload,
      onActionDelete: viewModel.handleDocumentoDeleteModalOpenState,
    });
  }, [
    viewModel.handleDocumentoModalOpenState,
    viewModel.handleDocumentoDownload,
    viewModel.handleDocumentoDeleteModalOpenState,
  ]);

  const qualificacoesColumns = useMemo(() => {
    return GenerateFornecedorQualificacaoColumnDefinition({
      onActionToggleQualificacao: viewModel.handleToggleQualificacao,
      onActionObservacaoChange: viewModel.handleObservacaoChange,
    });
  }, [
    viewModel.handleToggleQualificacao,
    viewModel.handleObservacaoChange,
  ]);

  const pinnedBancoColumns = useMemo((): GridPinnedColumnFields => {
    return viewModel.bancos && viewModel.bancos.length > 0
      ? { right: ['idBanco'] }
      : EMPTY_PINNED_COLUMN_FIELDS;
  }, [
    viewModel.bancos,
  ]);

  const pinnedAvaliacoesColumns = useMemo((): GridPinnedColumnFields => {
    return viewModel.avaliacoes && viewModel.avaliacoes.length > 0
      ? { right: ['idAvaliacaoFornecedor'] }
      : EMPTY_PINNED_COLUMN_FIELDS;
  }, [
    viewModel.avaliacoes,
  ]);

  const pinnedDocumentosColumns = useMemo((): GridPinnedColumnFields => {
    return viewModel.documentos && viewModel.documentos.length > 0
      ? { right: ['idFornecedorArquivo'] }
      : EMPTY_PINNED_COLUMN_FIELDS;
  }, [
    viewModel.documentos,
  ]);

  return (
    <>
      {/**
        * PageTopBar
        */}

      <PageTopBar>
        <ThreeSlotLayout
          rightSlot={
            <div className="flex flex-row gap-2">
              <div className="w-[115px]">
                <PopperButton
                  buttonLabel='Opções'
                  buttonColor='yellow'
                  options={viewModel.buttonOptions}
                  onSelect={viewModel.handlePopperActions}
                />
              </div>
              {
                viewModel.fornecedor &&
                <>
                  {
                    viewModel.fornecedor.desqualificado
                      ? (
                        <div className="w-[8.5rem]">
                          <PrimaryButton
                            buttonColor='primary-green'
                            startIcon={<DynamicIcon nameIcon="ThumbUp" size={25} className="text-white" />}
                            onClick={viewModel.handleToggleDesqualificado}>
                            Qualificar
                          </PrimaryButton>
                        </div>
                      ) : (
                        <div className="w-[8.5rem]">
                          <PrimaryButton
                            buttonColor='red'
                            startIcon={<DynamicIcon nameIcon="ThumbDown" size={25} className="text-white" />}
                            onClick={viewModel.handleToggleDesqualificado}>
                            Desqualificar
                          </PrimaryButton>
                        </div>
                      )
                  }
                </>
              }
              <BackButton
                onClick={viewModel.handleBack}
              />
            </div>
          }
        />
      </PageTopBar>

      {/**
        * Page
        */}

      <form onSubmit={viewModel.handleSubmit}>
        <div className="flex w-full gap-6 flex-col p-4 pt-8 mb-20">
          <Breadcrumbs aria-label="breadcrumb" className="cursor-pointer">
            <Link underline="hover" color="inherit">
              Compras
            </Link>
            <Link underline="hover" color="inherit" component={'span'}>
              <LinkTo to='/Fornecedores_lista'>
                Fornecedores
              </LinkTo>
            </Link>
            <Typography className="text-primary-green">
              Cadastro de Fornecedores
            </Typography>
          </Breadcrumbs>

          <div className="flex flex-col gap-2">
            <div className="div flex justify-between items-center flex-col md:flex-row gap-6 md:gap-0">
              <div className="flex gap-2 items-center w-full">
                <h1 className="text-3xl font-medium text-black dark:text-white">Cadastro de Fornecedores</h1>
                <RefreshButton onClick={viewModel.handleReload} />
              </div>

              <div className="w-full justify-end flex gap-2">
              </div>
            </div>
          </div>
          {
            viewModel.errorMessage !== undefined
              ? (
                <div className="w-full">
                  <AlertError
                    messageErro={viewModel.errorMessage}
                    onTryAgain={viewModel.handleReload} />
                </div>
              ) : (
                <div className="flex flex-col gap-4">
                  <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode p-4">
                    <div className="w-full grid grid-cols-2 sm:grid-cols-2 md:grid-cols-2 2xl:grid-cols-4 gap-4">
                      <div className="flex flex-row items-center gap-2">
                        <span className="text-black dark:text-white">
                          Status
                        </span>
                        <FormSwitchInput
                          name='ativo'
                          readOnly={viewModel.saving}
                          reactHookForm={viewModel.hookForm}
                        />
                      </div>

                      <div className="flex flex-row items-center gap-2">
                        <span className="text-black dark:text-white">
                          Avaliar Fornecedor?
                        </span>
                        <FormSwitchInput
                          name='avaliar'
                          readOnly={viewModel.saving}
                          activeLabel="Sim"
                          inactiveLabel="Não"
                          reactHookForm={viewModel.hookForm}
                        />
                      </div>

                      <div className="flex flex-row items-center gap-2">
                        <span className="text-black dark:text-white">
                          Fornecedor de Produtos?
                        </span>
                        <FormSwitchInput
                          name='fornProduto'
                          readOnly={viewModel.saving}
                          activeLabel="Sim"
                          inactiveLabel="Não"
                          reactHookForm={viewModel.hookForm}
                        />
                      </div>

                      <div className="flex flex-row items-center gap-2">
                        <span className="text-black dark:text-white">
                          Fornecedor de Serviços?
                        </span>
                        <FormSwitchInput
                          name='fornServico'
                          readOnly={viewModel.saving}
                          activeLabel="Sim"
                          inactiveLabel="Não"
                          reactHookForm={viewModel.hookForm}
                        />
                      </div>

                    </div>

                    <div className="w-full col-span-1 md:col-span-2 2xl:col-span-3 flex flex-row justify-end mt-8">
                      {
                        (viewModel.fornecedor !== null && viewModel.fornecedor.notaMedia !== null) && (
                          <div className="text-base text-black dark:text-white">
                            Nota média: <span
                              className="me-2 px-2 py-1 rounded bg-[#89c4f4] text-white text-sm"
                              style={{ textShadow: '-1px -1px 0 #000, 1px -1px 0 #000, -1px 1px 0 #000, 1px 1px 0 #000' }}>
                              {viewModel.fornecedor.notaMedia.toFixed(2).replaceAll('.', ',')}
                            </span>
                          </div>
                        )
                      }
                    </div>

                    <hr className="border-[1px] border-border-light-mode dark:border-border-dark-mode mt-4 mb-8" />

                    <div className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 my-2">
                      <div className="flex flex-row justify-start items-center gap-3 " >
                        <FormCNPJMaskedInput
                          name='cnpj'
                          label='CNPJ'
                          reactHookForm={viewModel.hookForm}
                          onBlur={() => viewModel.handleGetFornecedorDadosByCnpj(viewModel.hookForm.getValues('cnpj'))}
                        />

                        <div className="">
                          <Tooltip
                            title="Digite o CNPJ para preenchimento automático dos dados!"
                            slotProps={{ tooltip: { sx: { fontSize: '0.90rem' } } }}
                          >
                            <div className="flex items-center justify-center w-7 h-7 bg-blue-500 rounded-full text-white text-sm font-bold cursor-help">
                              ?
                            </div>
                          </Tooltip>
                        </div>

                      </div>

                      <div className="w-full col-span-1 2xl:col-span-2">
                        <DefaultFormInput
                          name='razaoSocial'
                          label='Razão Social'
                          reactHookForm={viewModel.hookForm}
                        />
                      </div>
                    </div>

                    <div className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 my-2">
                      <div >
                        <DefaultFormInput
                          name='familia'
                          label='Família'
                          reactHookForm={viewModel.hookForm}
                        />
                      </div>
                      <div className="w-full col-span-1 2xl:col-span-2">
                        <DefaultFormInput
                          name='nomeFantasia'
                          label='Nome Fantasia'
                          required={true}
                          reactHookForm={viewModel.hookForm}
                        />
                      </div>
                    </div>

                    <div className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 my-2">
                      <FormCPFMaskedInput
                        name='cpf'
                        label='CPF'
                        reactHookForm={viewModel.hookForm}
                      />

                      <DefaultFormInput
                        name='contato'
                        label='Contato 1'
                        reactHookForm={viewModel.hookForm}
                      />

                      <DefaultFormInput
                        name='email'
                        label='Email 1'
                        reactHookForm={viewModel.hookForm}
                      />
                    </div>

                    <div className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 my-2">

                      <DefaultFormInput
                        name='contato1'
                        label='Contato 2'
                        reactHookForm={viewModel.hookForm}
                      />

                      <DefaultFormInput
                        name='email1'
                        label='Email 2'
                        reactHookForm={viewModel.hookForm}
                      />

                      <DefaultFormInput
                        name='inscricaoEstadual'
                        label='Inscrição Estadual'
                        reactHookForm={viewModel.hookForm}
                      />
                    </div>

                    <div className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 my-2">

                      <FormPhoneMaskedInput
                        name='telefoneFixo'
                        label='Tel Fixo'
                        type='fixo'
                        reactHookForm={viewModel.hookForm}
                      />

                      <DefaultFormInput
                        name='inscricaoMunicipal'
                        label='Inscrição Municipal'
                        reactHookForm={viewModel.hookForm}
                      />

                      <FormPhoneMaskedInput
                        name='telefoneCelular'
                        label='Tel Celular'
                        type='movel'
                        reactHookForm={viewModel.hookForm}
                      />
                    </div>

                    <div className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 my-2">

                      <DefaultFormInput
                        name='atividade'
                        label='Atividade'
                        reactHookForm={viewModel.hookForm}
                      />

                      <FormDatePickerInput
                        name='dataCadastro'
                        label='Data Cadastro'
                        readOnly={true}
                        reactHookForm={viewModel.hookForm}
                      />

                      <div className="flex flex-row items-center gap-1">
                        <div className="w-[90%]">
                          <DefaultFormAutocomplete
                            name='idCentroCusto'
                            label='Centro de Custo'
                            options={viewModel.centroCustoDropdown}
                            getOptionKey={x => x.id}
                            getOptionLabel={x => x.title}
                            reactHookForm={viewModel.hookForm}
                          />
                        </div>
                        <Tooltip
                          title="Preencher somente se for controlar fornecedor por centro de custo!"
                          slotProps={{ tooltip: { sx: { fontSize: '0.90rem' } } }}
                        >
                          <div className="flex items-center justify-center w-7 h-7 bg-blue-500 rounded-full text-white text-sm font-bold cursor-help">
                            ?
                          </div>
                        </Tooltip>
                      </div>



                    </div>

                    <div className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 my-2">
                      <div className="w-full col-span-1 2xl:col-span-2">
                        <DefaultFormInput
                          name='avaliacaoFinanceira'
                          label='Avaliação Financeira'
                          reactHookForm={viewModel.hookForm}
                        />
                      </div>
                      <FormDatePickerInput
                        name='dataValidade'
                        label='Validade'
                        reactHookForm={viewModel.hookForm}
                      />
                    </div>


                    <div className="w-full flex flex-col mt-8">
                      <span className="text-black dark:text-white text-2xl font-semibold">
                        Endereço
                      </span>
                      <div className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3 gap-4 mt-4">
                        <FormMaskedInput
                          name='cep'
                          label='CEP'
                          mask='00000-000'
                          maskPlaceholder='_'
                          onChange={e => viewModel.handleCepChange(e)}
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormInput
                          name='cidade'
                          label='Cidade'
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormInput
                          name='rua'
                          label='Rua/Bairro/Alameda'
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormInput
                          name='numero'
                          label='Número'
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormInput
                          name='apartamento'
                          label='Complemento'
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormInput
                          name='bairro'
                          label='Bairro'
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormInput
                          name='uf'
                          label='Estado'
                          reactHookForm={viewModel.hookForm}
                        />

                        <DefaultFormInput
                          name='pais'
                          label='País'
                          reactHookForm={viewModel.hookForm}
                        />
                      </div>
                    </div>
                    {
                      viewModel.idFornecedor !== undefined &&
                      <div className="w-full flex flex-col mt-8 mb-4">
                        <div className="flex flex-row items-center justify-between">
                          <span className="text-black dark:text-white text-2xl font-semibold mb-4">
                            Bancos
                          </span>

                          <div>
                            <PrimaryButton
                              buttonColor="primary-green"
                              startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                              onMouseDown={e => {
                                viewModel.handleBancoModalOpenState(null, true);
                                e.preventDefault();
                              }}>
                              Novo Item
                            </PrimaryButton>
                          </div>
                        </div>
                        <Stack spacing={0}>
                          <StyledDataGrid
                            tableName="fornecedores_cadastro_banco"
                            disableColumnMenu={false}
                            autoHeight={true}
                            height="50vh"
                            getRowHeight={() => 'auto'}
                            getRowId={(row: FornecedorBanco) => row.idBanco}
                            columns={bancoColumns}
                            loading={viewModel.loading}
                            disableRowSelectionOnClick={true}
                            rows={viewModel.bancos}
                            paginationMode="client"
                            pinnedColumns={pinnedBancoColumns}
                          />
                        </Stack>
                      </div>
                    }
                  </div>

                  <CascateList title="Avaliações do Fornecedor" defaultOpen={false} textColor="orange" fontWeight="bold">
                    <>
                      {
                        viewModel.idFornecedor !== undefined &&
                        <div className="w-full flex flex-col">
                          <Stack spacing={0}>
                            <CustomStyledDataGrid
                              tableName="fornecedores_cadastro_avaliacoes"
                              disableColumnMenu={false}
                              getRowId={(row: FornecedorAvaliacao) => row.idAvaliacaoFornecedor}
                              autoHeight={true}
                              columns={avaliacaoColumns}
                              loading={viewModel.loading}
                              mediaGeral={viewModel.mediaGeral}
                              disableRowSelectionOnClick={true}
                              rows={viewModel.avaliacoes}
                              paginationMode="client"
                              pinnedColumns={pinnedAvaliacoesColumns}
                            />
                          </Stack>
                        </div>
                      }
                    </>
                  </CascateList>

                  <div className="w-full bg-light-mode-2 dark:bg-dark-mode-2 rounded-[4px] border-[1px] border-border-light-mode dark:border-border-dark-mode p-4">
                    <div className="w-full grid grid-cols-1 md:grid-cols-2 2xl:grid-cols-3">
                    </div>

                    <div className="mt-4">
                      <DefaultFormInput
                        name='obsFornecedor'
                        label='Observações'
                        multiline={true}
                        rows={8}
                        reactHookForm={viewModel.hookForm}
                      />
                    </div>
                    {
                      viewModel.idFornecedor !== undefined &&
                      <div className="w-full flex flex-col mt-8 mb-4">
                        <div className="flex flex-row items-center justify-between">
                          <span className="text-black dark:text-white text-2xl font-semibold mb-4">
                            Documentos Diversos
                          </span>

                          <div>
                            <PrimaryButton
                              buttonColor="primary-green"
                              startIcon={<DynamicIcon nameIcon="AddRounded" size={25} className="text-white" />}
                              onMouseDown={e => {
                                viewModel.handleDocumentoModalOpenState(null, true);
                                e.preventDefault();
                              }}>
                              Novo Item
                            </PrimaryButton>
                          </div>
                        </div>
                        <Stack spacing={0}>
                          <StyledDataGrid
                            tableName="fornecedores_cadastro_documentos"
                            disableColumnMenu={false}
                            autoHeight={true}
                            getRowId={(row: FornecedorDocumento) => row.idFornecedorArquivo}
                            columns={documentoColumns}
                            loading={viewModel.loading}
                            disableRowSelectionOnClick={true}
                            rows={viewModel.documentos}
                            paginationMode="client"
                            pinnedColumns={pinnedDocumentosColumns}
                          />
                        </Stack>
                      </div>
                    }
                    <div className="mt-4">
                      <DefaultFormInput
                        name='descQualificacoes'
                        label='Qualificações'
                        multiline={true}
                        rows={8}
                        reactHookForm={viewModel.hookForm}
                      />
                    </div>
                    {
                      <div className="w-full flex flex-col mt-8 mb-4">
                        <Stack spacing={0}>
                          <StyledDataGrid
                            tableName="fornecedores_cadastro_qualificacoes"
                            getRowHeight={() => 'auto'}
                            disableColumnMenu={false}
                            getRowId={(row: FornecedorQualificacao) => row.idQualificacao}
                            columns={qualificacoesColumns}
                            loading={viewModel.loading}
                            disableRowSelectionOnClick={true}
                            rows={viewModel.qualificacoes}
                          />
                        </Stack>
                      </div>
                    }
                  </div>
                </div>
              )
          }
        </div>

        {/**
          * BottomBar
          */}

        <BottomBar>
          <div className="w-full h-full items-center justify-between flex p-4 gap-4">
            <div>
              {
                isValidId(viewModel.idFornecedor) &&
                <PrimaryButton
                  buttonColor="yellow"
                  onClick={() => viewModel.handleOpenChangeHistory(null, null)}>
                  <span className="text-xs">
                    Historico de alterações
                  </span>
                </PrimaryButton>
              }
            </div>

            <div className="flex flex-row gap-4">
              <div className="p-2 hover:bg-hover-light-gray cursor-pointer dark:hover:bg-border-dark-gray rounded-[4px] font-semibold">
                <h1
                  className="text-sm text-black dark:text-white"
                  onClick={viewModel.handleBack}>
                  Cancelar
                </h1>
              </div>
              <div className="w-[130px]">
                {viewModel.saving
                  ? (
                    <PrimaryButton>
                      Salvando...
                    </PrimaryButton>
                  ) : (
                    <PrimaryButton
                      type="submit"
                      startIcon={<DynamicIcon nameIcon="CheckRounded" size={25} className="text-white" />}>
                      Salvar
                    </PrimaryButton>
                  )}
              </div>
            </div>
          </div>
        </BottomBar>
      </form>

      {/**
        * Modals
        */}

      <FornecedorBancoModal
        open={viewModel.bancoModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        selectedBanco={viewModel.selectedBanco}
        onClose={() => viewModel.handleBancoModalOpenState(null, false)}
        onSubmit={viewModel.handleBancoSubmit}
      />

      <FornecedorAvaliacaoModal
        open={viewModel.avaliacaoModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        selectedAvaliacao={viewModel.selectedAvaliacao}
        fornecedorAvaliacaoItems={viewModel.fornecedorAvaliacaoItems}
        funcionariosDropdown={viewModel.funcionariosDropdown}
        onClose={() => viewModel.handleAvaliacaoModalOpenState(null, false)}
        onSubmit={viewModel.handleAvaliacaoSubmit}
      />

      <FornecedorDocumentoModal
        open={viewModel.documentoModal}
        saving={viewModel.saving}
        loading={viewModel.loading}
        selectedDocumento={viewModel.selectedDocumento}
        onClose={() => viewModel.handleDocumentoModalOpenState(null, false)}
        onSubmit={viewModel.handleDocumentoSubmit}
        onDownloadFile={viewModel.handleDocumentoDownload}
      />

      <ChangeHistoryModal
        changeHistoryHandler={viewModel.changeHistory}
      />

      <DeleteDialog
        open={viewModel.fornecedorDeleteModal}
        saving={viewModel.saving}
        title='Você realmente deseja excluir este item?'
        onClose={() => viewModel.handleFornecedorDeleteModalOpenState(false)}
        onDelete={() => viewModel.handleFornecedorDeleteSubmit()}
      />

      <DeleteDialog
        open={viewModel.bancoDeleteModal}
        saving={viewModel.saving}
        title='Você realmente deseja excluir este item?'
        onClose={() => viewModel.handleBancoDeleteModalOpenState(null, false)}
        onDelete={() => viewModel.handleBancoDeleteSubmit()}
      />

      <DeleteDialog
        open={viewModel.avaliacaoDeleteModal}
        saving={viewModel.saving}
        title='Você realmente deseja excluir este item?'
        onClose={() => viewModel.handleAvaliacaoDeleteModalOpenState(null, false)}
        onDelete={() => viewModel.handleAvaliacaoDeleteSubmit()}
      />

      <DeleteDialog
        open={viewModel.documentoDeleteModal}
        saving={viewModel.saving}
        title='Você realmente deseja excluir este item?'
        onClose={() => viewModel.handleDocumentoDeleteModalOpenState(null, false)}
        onDelete={() => viewModel.handleDocumentoDeleteSubmit()}
      />

    </>
  );
}